// src/modules/ida/services/heartbeats.ts
//
// What? API surface for the heartbeat management UI.
// Why? Phase 2b floating panel + editor need list / get / edit / cancel.
// How? Wraps the backend's /api/workspace/{workspace_id}/scheduler/tasks
//      proxy routes (see metalmind_rag_saas_api/.../workspace/scheduler.py).
//      All endpoints are admin-gated on the backend -- non-admin callers
//      get 403 and the UI hides the panel.

export interface HeartbeatSummary {
  task_id: string;
  kind: "cron";
  status: string;
  next_fire_at: string;
  prompt: string | null;
  conversation_id: number | null;
}

export interface HeartbeatDetail extends HeartbeatSummary {
  cron_expression?: string;
  timezone?: string;
  agent_model?: string | null;
  // Any additional hub fields roundtripped as-is.
  [key: string]: unknown;
}

export interface HeartbeatListResponse {
  items: HeartbeatSummary[];
  scheduler_available: boolean;
}

export interface HeartbeatEditPayload {
  cron_expression: string;
  prompt: string;
  timezone: string;
  agent_model?: string | null;
  conversation_id?: number | null;
}

export interface HeartbeatEditResponse {
  status: "replaced";
  previous_task_id: string;
  task_id: string;
  next_fire_at: string;
}

export const heartbeatApi = {
  list(
    authFetch: (url: string, tag: string, init?: RequestInit) => Promise<any>,
    workspaceId: string,
    conversationId?: number | null,
  ): Promise<HeartbeatListResponse> {
    const qs = conversationId ? `?conversation_id=${conversationId}` : "";
    return authFetch(
      `/api/workspace/${workspaceId}/scheduler/tasks${qs}`,
      "heartbeat list",
    );
  },

  get(
    authFetch: (url: string, tag: string, init?: RequestInit) => Promise<any>,
    workspaceId: string,
    taskId: string,
  ): Promise<HeartbeatDetail> {
    return authFetch(
      `/api/workspace/${workspaceId}/scheduler/tasks/${taskId}`,
      "heartbeat get",
    );
  },

  cancel(
    authFetch: (url: string, tag: string, init?: RequestInit) => Promise<any>,
    workspaceId: string,
    taskId: string,
  ): Promise<{ status: "cancelled"; task_id: string }> {
    return authFetch(
      `/api/workspace/${workspaceId}/scheduler/tasks/${taskId}`,
      "heartbeat cancel",
      { method: "DELETE" },
    );
  },

  edit(
    authFetch: (url: string, tag: string, init?: RequestInit) => Promise<any>,
    workspaceId: string,
    taskId: string,
    payload: HeartbeatEditPayload,
  ): Promise<HeartbeatEditResponse> {
    return authFetch(
      `/api/workspace/${workspaceId}/scheduler/tasks/${taskId}`,
      "heartbeat edit",
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );
  },
};
